import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Building2, Plus, Trash2, Edit3, X, Save, Eye,
  Image, Video, Users, Award, FileCheck,
  Sparkles, Target, Briefcase, HeartHandshake,
  Flag, Package, ExternalLink, Palette, ChevronLeft, ChevronRight,
  ClipboardList, BookOpen, Newspaper, Globe, Tv
} from 'lucide-react';
import {
  Button, MessagePlugin, Input, Switch, Checkbox, Textarea, Tabs
} from 'tdesign-react';
import ProductImageUploader from '../../components/ProductImageUploader';
import ProductRichTextEditor from '../../components/ProductRichTextEditor';
import { DynamicIcon, IconPicker, ColorPickerField } from './components/CommonComponents';
import { ImagePreviewProvider, PreviewableImage } from './components/ImagePreview';
import { enterpriseSiteApi, tokenStorage } from '../../api';
import { getOrgLabel } from './constants';

// ============ 类型定义 ============
interface ImageTextItem {
  id: string;
  image: string;
  title: string;
  description: string;
}

interface IconTextItem {
  id: string;
  icon: string;
  iconBgColor: string;
  title: string;
  description: string;
}

// 共建共治合作单位：一个总数量 + 多张图片
interface CoGovernance {
  count: number;
  images: string[];
}

// 新媒体账号（公众号 / 视频号 / 其他媒体账号）
interface MediaAccount {
  id: string;
  image: string;
  name: string;
}

// 特殊群体服务
interface SpecialGroupService {
  id: string;
  name: string;
  description: string;
}

// 意见反馈/监督渠道
interface FeedbackChannel {
  id: string;
  name: string;
  phone: string;
  mailbox: string;
}

// 节假日值班安排
interface HolidayDuty {
  id: string;
  description: string;
  time: string;
  phone: string;
}

// 新媒体与补充信息
interface MediaAndSupplement {
  wechatOfficial: MediaAccount;
  wechatVideo: MediaAccount;
  otherMediaAccounts: MediaAccount[];
  specialGroupServices: SpecialGroupService[];
  feedbackChannels: FeedbackChannel[];
  holidayDuties: HolidayDuty[];
  otherContent: string;
}

// 官方网站
interface OfficialWebsite {
  name: string;
  url: string;
}

// 补充信息（选填）
interface SupplementInfo {
  officialWebsite: OfficialWebsite;
  socialMediaAccounts: MediaAccount[];
  otherContent: string;
}

// 政务服务信息
interface GovPublicAccount {
  name: string;
  qrcode: string;
}

interface GovOnlineEntry {
  name: string;
  image: string;
}

interface GovServiceInfo {
  serviceTime: string;
  serviceHotlines: string[];
  trafficGuides: string[];
  publicAccount: GovPublicAccount;
  onlineEntries: GovOnlineEntry[];
}

// 单位基本信息
interface UnitPositionItem {
  id: string;
  icon: string;
  title: string;
}

interface UnitDepartmentItem {
  id: string;
  title: string;
  description: string;
  phone: string;
}

interface UnitIntro {
  unitName: string;
  unitTypes: string[];
  introduction: string;
  unitPositioning: UnitPositionItem[];
  jurisdictionOverview: string;
  departments: UnitDepartmentItem[];
}

interface ConfigData {
  id: number | null;
  companyName: string;
  companyUrl: string;
  companyImages: string[];
  companyDesc: string;
  selectedSections: string[];
  cultureShow: ImageTextItem[];
  honors: ImageTextItem[];
  leadershipCare: ImageTextItem[];
  qualifications: ImageTextItem[];
  investmentProjects: IconTextItem[];
  companyStrength: IconTextItem[];
  cooperation: ImageTextItem[];
  partyBuildingNews: ImageTextItem[];
  productIntro: ImageTextItem[];
  // 特色服务与重点工作（多个：图片 + 标题 + 富文本描述，可见范围与产品介绍一致）
  featuredBiz: ImageTextItem[];
  // 重点工作/民生实事（多个：图片 + 标题 + 富文本描述）
  keyLivelihood: ImageTextItem[];
  // 便民服务指南（多个：图片 + 标题 + 富文本描述）
  serviceGuide: ImageTextItem[];
  // 共建共治合作单位（总数量 + 多张图片）
  coGovernance: CoGovernance;
  // 新媒体与补充信息
  mediaAndSupplement: MediaAndSupplement;
  // 补充信息（选填）
  supplementInfo: SupplementInfo;
  // 政务服务信息（独立板块，默认展示）
  govService: GovServiceInfo;
  // 单位基本信息（独立板块，默认展示）
  unitIntro: UnitIntro;
  promoVideo: string;
  updatedAt: string;
}

// ============ 自定义选择配置选项 ============
// orgType: 1 = 企业；其他 = 政务/其他类型（重点工作/便民服务等）
const sectionOptions = [
  { value: 'cultureShow', label: '文化展示', icon: Sparkles },
  { value: 'honors', label: '荣誉资质', icon: Award },
  { value: 'leadershipCare', label: '领导关怀', icon: Users },
  { value: 'qualifications', label: '经营资质', icon: FileCheck },
  { value: 'investmentProjects', label: '投资项目', icon: Target },
  { value: 'companyStrength', label: '企业实力', icon: Building2 },
  { value: 'cooperation', label: '合作情况', icon: HeartHandshake },
  { value: 'partyBuildingNews', label: '党建动态', icon: Flag },
  { value: 'productIntro', label: '产品介绍', icon: Package },
  { value: 'featuredBiz', label: '特色服务与重点工作', icon: Briefcase },
  { value: 'keyLivelihood', label: '重点工作/民生实事', icon: ClipboardList },
  { value: 'serviceGuide', label: '便民服务指南', icon: BookOpen },
  { value: 'coGovernance', label: '共建共治合作单位', icon: Users },
  { value: 'promoVideo', label: '宣传视频', icon: Video },
  { value: 'newMedia', label: '新媒体', icon: Tv },
  { value: 'supplementInfo', label: '补充信息', icon: Newspaper },
];

// ============ 工具函数 ============
const genId = () => Date.now().toString() + Math.random().toString(36).slice(2, 6);

const emptyImageTextItem = (): ImageTextItem => ({
  id: genId(), image: '', title: '', description: ''
});

const emptyIconTextItem = (): IconTextItem => ({
  id: genId(), icon: 'star', iconBgColor: '#1c5bff', title: '', description: ''
});

const emptyMediaAccount = (): MediaAccount => ({
  id: genId(), image: '', name: ''
});

const emptyMediaAndSupplement = (): MediaAndSupplement => ({
  wechatOfficial: emptyMediaAccount(),
  wechatVideo: emptyMediaAccount(),
  otherMediaAccounts: [],
  specialGroupServices: [],
  feedbackChannels: [],
  holidayDuties: [],
  otherContent: ''
});

const emptyOfficialWebsite = (): OfficialWebsite => ({ name: '', url: '' });

const emptySupplementInfo = (): SupplementInfo => ({
  officialWebsite: emptyOfficialWebsite(),
  socialMediaAccounts: [],
  otherContent: ''
});

const emptyGovService = (): GovServiceInfo => ({
  serviceTime: '',
  serviceHotlines: [''],
  trafficGuides: [''],
  publicAccount: { name: '', qrcode: '' },
  onlineEntries: [],
});

const emptyUnitIntro = (): UnitIntro => ({
  unitName: '',
  unitTypes: [],
  introduction: '',
  unitPositioning: [],
  jurisdictionOverview: '',
  departments: [],
});

const emptyConfig = (): ConfigData => ({
  id: null,
  companyName: '',
  companyUrl: '',
  companyImages: [],
  companyDesc: '',
  selectedSections: [],
  cultureShow: [],
  honors: [],
  leadershipCare: [],
  qualifications: [],
  investmentProjects: [],
  companyStrength: [],
  cooperation: [],
  partyBuildingNews: [],
  productIntro: [],
  featuredBiz: [],
  keyLivelihood: [],
  serviceGuide: [],
  coGovernance: { count: 0, images: [] },
  mediaAndSupplement: emptyMediaAndSupplement(),
  supplementInfo: emptySupplementInfo(),
  govService: emptyGovService(),
  unitIntro: emptyUnitIntro(),
  promoVideo: '',
  updatedAt: ''
});

// URL 正则校验
const URL_REGEX = /^https?:\/\/([\w-]+\.)+[\w-]+(\/[\w\-./?%&=]*)?$/;
const isValidUrl = (url: string) => !url || URL_REGEX.test(url);

// ============ API ↔ ConfigData 转换 ============
const fromApiImageTextItem = (item: any): ImageTextItem => ({
  id: item?.id ? String(item.id) : genId(),
  image: item?.image || '',
  title: item?.title || '',
  description: item?.description || '',
});

const fromApiIconTextItem = (item: any): IconTextItem => ({
  id: item?.id ? String(item.id) : genId(),
  icon: item?.icon || 'star',
  iconBgColor: item?.bgColor || '#1c5bff',
  title: item?.title || '',
  description: item?.description || '',
});

const toApiImageTextItem = (item: ImageTextItem) => ({
  image: item.image,
  title: item.title,
  description: item.description,
});

const toApiIconTextItem = (item: IconTextItem) => ({
  icon: item.icon,
  bgColor: item.iconBgColor,
  title: item.title,
  description: item.description,
});

const mapApiToMediaAndSupplement = (data: any): MediaAndSupplement => {
  const raw = data || {};
  return {
    wechatOfficial: {
      id: genId(),
      image: raw.wechatOfficial?.image || '',
      name: raw.wechatOfficial?.name || '',
    },
    wechatVideo: {
      id: genId(),
      image: raw.wechatVideo?.image || '',
      name: raw.wechatVideo?.name || '',
    },
    otherMediaAccounts: Array.isArray(raw.otherMediaAccounts)
      ? raw.otherMediaAccounts.map((item: any) => ({
          id: genId(),
          image: item?.image || '',
          name: item?.name || '',
        }))
      : [],
    specialGroupServices: Array.isArray(raw.specialGroupServices)
      ? raw.specialGroupServices.map((item: any) => ({
          id: genId(),
          name: item?.name || '',
          description: item?.description || '',
        }))
      : [],
    feedbackChannels: Array.isArray(raw.feedbackChannels)
      ? raw.feedbackChannels.map((item: any) => ({
          id: genId(),
          name: item?.name || '',
          phone: item?.phone || '',
          mailbox: item?.mailbox || '',
        }))
      : [],
    holidayDuties: Array.isArray(raw.holidayDuties)
      ? raw.holidayDuties.map((item: any) => ({
          id: genId(),
          description: item?.description || '',
          time: item?.time || '',
          phone: item?.phone || '',
        }))
      : [],
    otherContent: raw.otherContent || '',
  };
};

const mapMediaAndSupplementToApi = (data: MediaAndSupplement): any => ({
  wechatOfficial: {
    image: data.wechatOfficial.image,
    name: data.wechatOfficial.name,
  },
  wechatVideo: {
    image: data.wechatVideo.image,
    name: data.wechatVideo.name,
  },
  otherMediaAccounts: data.otherMediaAccounts.map(item => ({
    image: item.image,
    name: item.name,
  })),
  specialGroupServices: data.specialGroupServices.map(item => ({
    name: item.name,
    description: item.description,
  })),
  feedbackChannels: data.feedbackChannels.map(item => ({
    name: item.name,
    phone: item.phone,
    mailbox: item.mailbox,
  })),
  holidayDuties: data.holidayDuties.map(item => ({
    description: item.description,
    time: item.time,
    phone: item.phone,
  })),
  otherContent: data.otherContent,
});

const mapApiToSupplementInfo = (data: any): SupplementInfo => {
  const raw = data || {};
  return {
    officialWebsite: {
      name: raw.officialWebsite?.name || '',
      url: raw.officialWebsite?.url || '',
    },
    socialMediaAccounts: Array.isArray(raw.socialMediaAccounts)
      ? raw.socialMediaAccounts.map((item: any) => ({
          id: genId(),
          image: item?.image || '',
          name: item?.name || '',
        }))
      : [],
    otherContent: raw.otherContent || '',
  };
};

const mapSupplementInfoToApi = (data: SupplementInfo): any => ({
  officialWebsite: {
    name: data.officialWebsite.name,
    url: data.officialWebsite.url,
  },
  socialMediaAccounts: data.socialMediaAccounts.map(item => ({
    image: item.image,
    name: item.name,
  })),
  otherContent: data.otherContent,
});

const mapApiToGovService = (data: any): GovServiceInfo => {
  const raw = data || {};
  return {
    serviceTime: raw.serviceTime || '',
    serviceHotlines: Array.isArray(raw.serviceHotlines) ? [...raw.serviceHotlines] : [''],
    trafficGuides: Array.isArray(raw.trafficGuides) ? [...raw.trafficGuides] : [''],
    publicAccount: {
      name: raw.publicAccount?.name || '',
      qrcode: raw.publicAccount?.qrcode || '',
    },
    onlineEntries: Array.isArray(raw.onlineEntries)
      ? raw.onlineEntries.map((item: any) => ({
          name: item?.name || '',
          image: item?.image || '',
        }))
      : [],
  };
};

const mapGovServiceToApi = (data: GovServiceInfo): any => ({
  serviceTime: data.serviceTime,
  serviceHotlines: data.serviceHotlines.filter(Boolean),
  trafficGuides: data.trafficGuides.filter(Boolean),
  publicAccount: {
    name: data.publicAccount.name,
    qrcode: data.publicAccount.qrcode,
  },
  onlineEntries: data.onlineEntries.map(item => ({
    name: item.name,
    image: item.image,
  })),
});

const mapApiToUnitIntro = (data: any): UnitIntro => {
  const raw = data || {};
  return {
    unitName: raw.unitName || '',
    unitTypes: (() => {
    const v = raw.unitTypes;
    if (Array.isArray(v)) return [...v];
    if (typeof v === 'string' && v.trim()) return v.split(/[,，、]/).map(s => s.trim()).filter(Boolean);
    return [];
  })(),
    introduction: raw.introduction || '',
    unitPositioning: Array.isArray(raw.unitPositioning)
      ? raw.unitPositioning.map((item: any) => ({
          id: genId(),
          icon: item?.icon || 'star',
          title: item?.title || item?.content || '',
        }))
      : [],
    jurisdictionOverview: raw.jurisdictionOverview || '',
    departments: Array.isArray(raw.departments)
      ? raw.departments.map((item: any) => ({
          id: genId(),
          title: item?.title || '',
          description: item?.description || '',
          phone: item?.phone || '',
        }))
      : [],
  };
};

const mapUnitIntroToApi = (data: UnitIntro): any => ({
  unitName: data.unitName,
  // 后端约定 unitTypes 以字符串（逗号分隔）传递
  unitTypes: data.unitTypes.length ? data.unitTypes.join(',') : undefined,
  introduction: data.introduction,
  unitPositioning: data.unitPositioning.map(item => ({
    icon: item.icon,
    title: item.title,
  })),
  jurisdictionOverview: data.jurisdictionOverview,
  departments: data.departments.map(item => ({
    title: item.title,
    description: item.description,
    phone: item.phone,
  })),
});

const mapApiToConfig = (apiData: any): ConfigData => {
  if (!apiData) return emptyConfig();

  const customConfig = apiData.customDisplayConfig || {};

  return {
    id: apiData.id ?? null,
    companyName: apiData.enterpriseName || '',
    companyUrl: apiData.siteUrl || '',
    companyImages: apiData.enterpriseImage ? apiData.enterpriseImage.split(',').filter(Boolean) : [],
    companyDesc: apiData.introduction || '',
    // 后端 customDisplayConfig 使用 featureService，前端选项值为 featuredBiz，此处做归一
    selectedSections: (Array.isArray(customConfig)
      ? customConfig
      : (customConfig.selectedSections || [])).map((v: string) => (v === 'featureService' ? 'featuredBiz' : v)),
    cultureShow: (apiData.cultureDisplay || []).map(fromApiImageTextItem),
    honors: (apiData.honors || []).map(fromApiImageTextItem),
    leadershipCare: (apiData.leadershipCare || []).map(fromApiImageTextItem),
    qualifications: (apiData.qualifications || []).map(fromApiImageTextItem),
    investmentProjects: (apiData.investmentProjects || []).map(fromApiIconTextItem),
    companyStrength: (apiData.enterpriseStrength || []).map(fromApiIconTextItem),
    cooperation: (apiData.cooperation || []).map(fromApiImageTextItem),
    partyBuildingNews: (apiData.partyBuilding || []).map(fromApiImageTextItem),
    productIntro: (apiData.productIntros || []).map(fromApiImageTextItem),
    featuredBiz: (apiData.featureServices || []).map(fromApiImageTextItem),
    keyLivelihood: (apiData.keyLivelihood || []).map(fromApiImageTextItem),
    serviceGuide: (apiData.serviceGuide || []).map(fromApiImageTextItem),
    coGovernance: {
      count: apiData.coGovernance?.count || 0,
      images: Array.isArray(apiData.coGovernance?.images) ? [...apiData.coGovernance.images] : [],
    },
    mediaAndSupplement: mapApiToMediaAndSupplement(apiData.mediaAndSupplement),
    supplementInfo: mapApiToSupplementInfo(apiData.supplementInfo),
    govService: mapApiToGovService(apiData.govService),
    unitIntro: mapApiToUnitIntro(apiData.unitIntro),
    promoVideo: apiData.promoVideo || '',
    updatedAt: apiData.updateTime || '',
  };
};

const mapConfigToApi = (config: ConfigData): any => {
  const tenantInfo = tokenStorage.getTenantInfo();

  const payload: any = {
    id: config.id ?? undefined,
    tenantId: tenantInfo?.tenantId ?? tenantInfo?.id,
    siteUrl: config.companyUrl,
    introduction: config.companyDesc,
    promoVideo: config.promoVideo,
    enterpriseName: config.companyName,
    enterpriseImage: config.companyImages.join(','),
    // 后端约定：customDisplayConfig 中「特色服务与重点工作」以 featureService 传递
    customDisplayConfig: config.selectedSections.map(v => (v === 'featuredBiz' ? 'featureService' : v)),
    cultureDisplay: config.cultureShow.map(toApiImageTextItem),
    honors: config.honors.map(toApiImageTextItem),
    leadershipCare: config.leadershipCare.map(toApiImageTextItem),
    qualifications: config.qualifications.map(toApiImageTextItem),
    investmentProjects: config.investmentProjects.map(toApiIconTextItem),
    enterpriseStrength: config.companyStrength.map(toApiIconTextItem),
    cooperation: config.cooperation.map(toApiImageTextItem),
    partyBuilding: config.partyBuildingNews.map(toApiImageTextItem),
    productIntros: config.productIntro.map(toApiImageTextItem),
    featureServices: config.featuredBiz.map(toApiImageTextItem),
    keyLivelihood: config.keyLivelihood.map(toApiImageTextItem),
    serviceGuide: config.serviceGuide.map(toApiImageTextItem),
    coGovernance: {
      count: config.coGovernance.count,
      images: config.coGovernance.images,
    },
    mediaAndSupplement: mapMediaAndSupplementToApi(config.mediaAndSupplement),
    supplementInfo: mapSupplementInfoToApi(config.supplementInfo),
    govService: mapGovServiceToApi(config.govService),
    unitIntro: mapUnitIntroToApi(config.unitIntro),
  };

  // 过滤空字段
  Object.keys(payload).forEach(key => {
    if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });

  return payload;
};

// ============ 通用组件 ============
function SectionCard({
  title,
  icon: Icon,
  children,
  action
}: {
  title: React.ReactNode;
  icon: any;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#1c5bff]" />
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function DisplayRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-800 font-medium text-right max-w-[60%] truncate">{value || '-'}</span>
    </div>
  );
}

// ============ 图片+标题+富文本 编辑组件 ============
function ImageRichEditor({
  items,
  onChange,
  colorClass = 'border-gray-200'
}: {
  items: ImageTextItem[];
  onChange: (items: ImageTextItem[]) => void;
  colorClass?: string;
}) {
  const add = () => onChange([...items, emptyImageTextItem()]);
  const remove = (id: string) => onChange(items.filter(i => i.id !== id));
  const clearAll = () => onChange([]);
  const update = (id: string, field: keyof ImageTextItem, value: any) => {
    onChange(items.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        {items.length > 0 && (
          <Button
            size="small"
            theme="danger"
            variant="outline"
            onClick={clearAll}
          >
            <span className="flex items-center gap-1"><Trash2 className="w-3 h-3" />清空</span>
          </Button>
        )}
        <Button size="small" theme="primary" variant="outline" onClick={add}>
          <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
          暂无内容，点击"添加"按钮新增
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={item.id} className={`flex gap-3 items-start p-3 rounded-lg border ${colorClass} transition-all relative`}>
              <button
                type="button"
                onClick={() => remove(item.id)}
                title="删除此条"
                className="absolute z-10 top-0 left-0 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors border border-gray-200 hover:border-red-500 bg-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="w-[50%] shrink-0">
                <ProductImageUploader
                  value={item.image}
                  onChange={(v) => update(item.id, 'image', v as string)}
                  simple
                />
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                  <Input
                    value={item.title}
                    onChange={(v) => update(item.id, 'title', v)}
                    placeholder="标题"
                    size="small"
                  />
                </div>
                <ProductRichTextEditor
                  value={item.description}
                  onChange={(v) => update(item.id, 'description', v)}
                  showHeight={false}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ 标题+富文本 编辑组件 ============
function TextRichEditor({
  items,
  onChange,
  colorClass = 'border-gray-200'
}: {
  items: ImageTextItem[];
  onChange: (items: ImageTextItem[]) => void;
  colorClass?: string;
}) {
  const add = () => onChange([...items, emptyImageTextItem()]);
  const remove = (id: string) => onChange(items.filter(i => i.id !== id));
  const clearAll = () => onChange([]);
  const update = (id: string, field: keyof ImageTextItem, value: any) => {
    onChange(items.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        {items.length > 0 && (
          <Button size="small" theme="danger" variant="outline" onClick={clearAll}>
            <span className="flex items-center gap-1"><Trash2 className="w-3 h-3" />清空</span>
          </Button>
        )}
        <Button size="small" theme="primary" variant="outline" onClick={add}>
          <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
          暂无内容，点击"添加"按钮新增
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((item, idx) => (
            <div key={item.id} className={`p-8 rounded-lg border ${colorClass} transition-all relative`}>
              <button
                type="button"
                onClick={() => remove(item.id)}
                title="删除此条"
                className="absolute z-10 top-0 left-0 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors border border-gray-200 hover:border-red-500 bg-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                <Input
                  value={item.title}
                  onChange={(v) => update(item.id, 'title', v)}
                  placeholder="标题（必填）"
                  size="small"
                />
              </div>
              <ProductRichTextEditor
                value={item.description}
                onChange={(v) => update(item.id, 'description', v)}
                showHeight={false}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ 图标+背景色+标题+富文本 编辑组件 ============
function IconRichEditor({
  items,
  onChange
}: {
  items: IconTextItem[];
  onChange: (items: IconTextItem[]) => void;
}) {
  const add = () => onChange([...items, emptyIconTextItem()]);
  const remove = (id: string) => onChange(items.filter(i => i.id !== id));
  const clearAll = () => onChange([]);
  const update = (id: string, field: keyof IconTextItem, value: any) => {
    onChange(items.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        {items.length > 0 && (
          <Button
            size="small"
            theme="danger"
            variant="outline"
            onClick={clearAll}
          >
            <span className="flex items-center gap-1"><Trash2 className="w-3 h-3" />清空</span>
          </Button>
        )}
        <Button size="small" theme="primary" variant="outline" onClick={add}>
          <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
          暂无内容，点击"添加"按钮新增
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((item, idx) => (
            <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-3 relative">
              <button
                type="button"
                onClick={() => remove(item.id)}
                title="删除此条"
                className="absolute z-10 top-0 left-0 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors border border-gray-200 hover:border-red-500 bg-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                项目 #{idx + 1}
              </div>
              <div className="space-y-2">
                <IconPicker
                  value={item.icon}
                  onChange={(v) => update(item.id, 'icon', v)}
                  label="图标"
                />
                <ColorPickerField
                  value={item.iconBgColor}
                  onChange={(v) => update(item.id, 'iconBgColor', v)}
                  label="背景色"
                />
                <div>
                  <label className="text-xs text-gray-500 block mb-1">标题</label>
                  <Input
                    value={item.title}
                    onChange={(v) => update(item.id, 'title', v)}
                    placeholder="请输入标题"
                    size="small"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">详细描述（富文本）</label>
                  <ProductRichTextEditor
                    value={item.description}
                    onChange={(v) => update(item.id, 'description', v)}
                    showHeight={false}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ 新媒体与补充信息 编辑组件 ============
function MediaAndSupplementEditor({
  value,
  onChange,
}: {
  value: MediaAndSupplement;
  onChange: (value: MediaAndSupplement) => void;
}) {
  const update = (patch: Partial<MediaAndSupplement>) => {
    onChange({ ...value, ...patch });
  };

  const updateArrayItem = <T extends { id: string }>(
    arr: T[],
    id: string,
    patch: Partial<T>,
    setter: (items: T[]) => void
  ) => {
    setter(arr.map(item => (item.id === id ? { ...item, ...patch } : item)));
  };

  return (
    <div className="space-y-5">
      {/* 微信公众号 + 微信视频号：并排，内容上下结构 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 微信公众号 */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center">1</span>
            微信公众号
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">公众号名称</label>
              <Input
                value={value.wechatOfficial.name}
                onChange={(v) => update({ wechatOfficial: { ...value.wechatOfficial, name: v } })}
                placeholder="如：XX街道办事处"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">二维码图片</label>
              <ProductImageUploader
                value={value.wechatOfficial.image}
                onChange={(v) => update({ wechatOfficial: { ...value.wechatOfficial, image: typeof v === 'string' ? v : '' } })}
                simple
              />
            </div>
          </div>
        </div>

        {/* 微信视频号 */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center">2</span>
            微信视频号
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">视频号名称</label>
              <Input
                value={value.wechatVideo.name}
                onChange={(v) => update({ wechatVideo: { ...value.wechatVideo, name: v } })}
                placeholder="如：XX街道官方视频号"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">头像 / 二维码</label>
              <ProductImageUploader
                value={value.wechatVideo.image}
                onChange={(v) => update({ wechatVideo: { ...value.wechatVideo, image: typeof v === 'string' ? v : '' } })}
                simple
              />
            </div>
          </div>
        </div>
      </div>

      {/* 其他新媒体账号 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center">3</span>
            其他新媒体账号
          </div>
          <Button size="small" theme="primary" variant="outline" onClick={() => update({ otherMediaAccounts: [...value.otherMediaAccounts, emptyMediaAccount()] })}>
            <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
          </Button>
        </div>
        {value.otherMediaAccounts.length === 0 ? (
          <div className="text-center py-5 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
            暂无其他新媒体账号，点击“添加”按钮新增
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {value.otherMediaAccounts.map((item) => (
              <div key={item.id} className="p-3 border border-gray-100 rounded-lg bg-gray-50/50 relative">
                <button
                  type="button"
                  onClick={() => update({ otherMediaAccounts: value.otherMediaAccounts.filter(i => i.id !== item.id) })}
                  className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors border border-gray-200 hover:border-red-500 bg-white"
                  title="删除"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <div className="space-y-3 pr-8">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">账号图片</label>
                    <ProductImageUploader
                      value={item.image}
                      onChange={(v) => updateArrayItem(value.otherMediaAccounts, item.id, { image: typeof v === 'string' ? v : '' }, (arr) => update({ otherMediaAccounts: arr }))}
                      simple
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">账号名称</label>
                    <Input
                      value={item.name}
                      onChange={(v) => updateArrayItem(value.otherMediaAccounts, item.id, { name: v }, (arr) => update({ otherMediaAccounts: arr }))}
                      placeholder="如：抖音号、微博号等"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 特殊群体服务 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center">4</span>
            特殊群体服务
          </div>
          <Button size="small" theme="primary" variant="outline" onClick={() => update({ specialGroupServices: [...value.specialGroupServices, { id: genId(), name: '', description: '' }] })}>
            <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
          </Button>
        </div>
        {value.specialGroupServices.length === 0 ? (
          <div className="text-center py-5 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
            暂无特殊群体服务，点击“添加”按钮新增
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {value.specialGroupServices.map((item) => (
              <div key={item.id} className="p-3 border border-gray-100 rounded-lg bg-gray-50/50 relative">
                <button
                  type="button"
                  onClick={() => update({ specialGroupServices: value.specialGroupServices.filter(i => i.id !== item.id) })}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors border border-gray-200 hover:border-red-500 bg-white"
                  title="删除"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="mb-2 pr-8">
                  <label className="text-xs text-gray-500 block mb-1">服务名称</label>
                  <Input
                    value={item.name}
                    onChange={(v) => updateArrayItem(value.specialGroupServices, item.id, { name: v }, (arr) => update({ specialGroupServices: arr }))}
                    placeholder="如：老年人绿色通道"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">服务介绍</label>
                  <Textarea
                    value={item.description}
                    onChange={(v) => updateArrayItem(value.specialGroupServices, item.id, { description: v }, (arr) => update({ specialGroupServices: arr }))}
                    placeholder="请输入服务介绍"
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 意见反馈/监督渠道 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">5</span>
            意见反馈 / 监督渠道
          </div>
          <Button size="small" theme="primary" variant="outline" onClick={() => update({ feedbackChannels: [...value.feedbackChannels, { id: genId(), name: '', phone: '', mailbox: '' }] })}>
            <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
          </Button>
        </div>
        {value.feedbackChannels.length === 0 ? (
          <div className="text-center py-5 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
            暂无反馈/监督渠道，点击“添加”按钮新增
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {value.feedbackChannels.map((item) => (
              <div key={item.id} className="p-3 border border-gray-100 rounded-lg bg-gray-50/50 relative">
                <button
                  type="button"
                  onClick={() => update({ feedbackChannels: value.feedbackChannels.filter(i => i.id !== item.id) })}
                  className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors border border-gray-200 hover:border-red-500 bg-white"
                  title="删除"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="space-y-3 pr-8">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">渠道名称</label>
                    <Input
                      value={item.name}
                      onChange={(v) => updateArrayItem(value.feedbackChannels, item.id, { name: v }, (arr) => update({ feedbackChannels: arr }))}
                      placeholder="如：纪检监察举报"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">联系电话</label>
                    <Input
                      value={item.phone}
                      onChange={(v) => updateArrayItem(value.feedbackChannels, item.id, { phone: v }, (arr) => update({ feedbackChannels: arr }))}
                      placeholder="如：028-12345678"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">意见箱地址</label>
                    <Input
                      value={item.mailbox}
                      onChange={(v) => updateArrayItem(value.feedbackChannels, item.id, { mailbox: v }, (arr) => update({ feedbackChannels: arr }))}
                      placeholder="如：XX街道办事处一楼大厅"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 节假日值班安排 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-purple-500 text-white text-[10px] flex items-center justify-center">6</span>
            节假日值班安排
          </div>
          <Button size="small" theme="primary" variant="outline" onClick={() => update({ holidayDuties: [...value.holidayDuties, { id: genId(), description: '', time: '', phone: '' }] })}>
            <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
          </Button>
        </div>
        {value.holidayDuties.length === 0 ? (
          <div className="text-center py-5 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
            暂无值班安排，点击“添加”按钮新增
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {value.holidayDuties.map((item) => (
              <div key={item.id} className="p-3 border border-gray-100 rounded-lg bg-gray-50/50 relative">
                <button
                  type="button"
                  onClick={() => update({ holidayDuties: value.holidayDuties.filter(i => i.id !== item.id) })}
                  className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors border border-gray-200 hover:border-red-500 bg-white"
                  title="删除"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="space-y-3 pr-8">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">值班描述</label>
                    <Input
                      value={item.description}
                      onChange={(v) => updateArrayItem(value.holidayDuties, item.id, { description: v }, (arr) => update({ holidayDuties: arr }))}
                      placeholder="如：法定节假日值班"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">值班时间</label>
                    <Input
                      value={item.time}
                      onChange={(v) => updateArrayItem(value.holidayDuties, item.id, { time: v }, (arr) => update({ holidayDuties: arr }))}
                      placeholder="如：9:00-12:00"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">值班电话</label>
                    <Input
                      value={item.phone}
                      onChange={(v) => updateArrayItem(value.holidayDuties, item.id, { phone: v }, (arr) => update({ holidayDuties: arr }))}
                      placeholder="如：028-12345678"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 其他需要展示的内容 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-gray-500 text-white text-[10px] flex items-center justify-center">7</span>
          其他需要展示的内容
        </div>
        <ProductRichTextEditor
          value={value.otherContent}
          showHeight={false}
          onChange={(v) => update({ otherContent: v })}
        />
      </div>
    </div>
  );
}

// ============ 补充信息（选填）编辑组件 ============
function SupplementInfoEditor({
  value,
  onChange,
}: {
  value: SupplementInfo;
  onChange: (value: SupplementInfo) => void;
}) {
  const update = (patch: Partial<SupplementInfo>) => {
    onChange({ ...value, ...patch });
  };

  const updateArrayItem = <T extends { id: string }>(
    arr: T[],
    id: string,
    patch: Partial<T>,
    setter: (items: T[]) => void
  ) => {
    setter(arr.map(item => (item.id === id ? { ...item, ...patch } : item)));
  };

  return (
    <div className="space-y-5">
      {/* 官方网站 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-indigo-500 text-white text-[10px] flex items-center justify-center">1</span>
          官方网站
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">网站名称</label>
            <Input
              value={value.officialWebsite.name}
              onChange={(v) => update({ officialWebsite: { ...value.officialWebsite, name: v } })}
              placeholder="如：XX公司官网"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">网站地址</label>
            <Input
              value={value.officialWebsite.url}
              onChange={(v) => update({ officialWebsite: { ...value.officialWebsite, url: v } })}
              placeholder="如：https://www.example.com"
            />
          </div>
        </div>
      </div>

      {/* 社交媒体账号 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-pink-500 text-white text-[10px] flex items-center justify-center">2</span>
            社交媒体账号
          </div>
          <Button size="small" theme="primary" variant="outline" onClick={() => update({ socialMediaAccounts: [...value.socialMediaAccounts, emptyMediaAccount()] })}>
            <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
          </Button>
        </div>
        {value.socialMediaAccounts.length === 0 ? (
          <div className="text-center py-5 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
            暂无社交媒体账号，点击“添加”按钮新增
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {value.socialMediaAccounts.map((item) => (
              <div key={item.id} className="p-3 border border-gray-100 rounded-lg bg-gray-50/50 relative">
                <button
                  type="button"
                  onClick={() => update({ socialMediaAccounts: value.socialMediaAccounts.filter(i => i.id !== item.id) })}
                  className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors border border-gray-200 hover:border-red-500 bg-white"
                  title="删除"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <div className="space-y-3 pr-8">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">账号图片</label>
                    <ProductImageUploader
                      value={item.image}
                      onChange={(v) => updateArrayItem(value.socialMediaAccounts, item.id, { image: typeof v === 'string' ? v : '' }, (arr) => update({ socialMediaAccounts: arr }))}
                      simple
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">账号名称</label>
                    <Input
                      value={item.name}
                      onChange={(v) => updateArrayItem(value.socialMediaAccounts, item.id, { name: v }, (arr) => update({ socialMediaAccounts: arr }))}
                      placeholder="如：微信公众号：XX科技"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 其他展示内容 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-gray-500 text-white text-[10px] flex items-center justify-center">3</span>
          其他展示内容
        </div>
        <ProductRichTextEditor
          value={value.otherContent}
          showHeight={false}
          onChange={(v) => update({ otherContent: v })}
        />
      </div>
    </div>
  );
}

// ============ 图片+标题 展示组件（不展示富文本） ============
function ImageRichDisplay({ items, emptyText }: { items: ImageTextItem[]; emptyText?: string }) {
  if (!items || items.length === 0) {
    return <div className="text-center py-4 text-gray-400 text-sm">{emptyText || '暂无数据'}</div>;
  }
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map((item, idx) => (
        <div key={item.id} className="rounded-xl overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow group">
          <div className="aspect-[4/3] overflow-hidden">
            {item.image ? (
              <PreviewableImage src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                暂无图片
              </div>
            )}
          </div>
          <div className="p-3">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold shrink-0">{idx + 1}</span>
              <span className="text-sm font-medium text-gray-800 truncate">{item.title || '未设置标题'}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ 图标+标题 展示组件（不展示富文本） ============
function IconRichDisplay({ items, emptyText }: { items: IconTextItem[]; emptyText?: string }) {
  if (!items || items.length === 0) {
    return <div className="text-center py-4 text-gray-400 text-sm">{emptyText || '暂无数据'}</div>;
  }
  return (
    <div className="grid grid-cols-4 gap-3">
      {items.map((item, idx) => (
        <div key={item.id} className="rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow p-4 flex flex-col items-center text-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
          </div>
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-2"
            style={{ backgroundColor: item.iconBgColor || '#1c5bff' }}
          >
            <DynamicIcon iconName={item.icon} className="w-6 h-6 text-white" />
          </div>
          <span className="text-sm font-medium text-gray-800 truncate w-full">{item.title || '未设置标题'}</span>
        </div>
      ))}
    </div>
  );
}

// ============ 主组件 ============
export default function MicrOfficialWebsite() {
  const [config, setConfig] = useState<ConfigData>(emptyConfig());
  const [isEditing, setIsEditing] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [activeTab, setActiveTab] = useState<string>('');
  const initializedRef = useRef(false);

  const tenantInfo = tokenStorage.getTenantInfo();
  const tenantLevel = tenantInfo?.tenantLevel;
  const orgType = tenantInfo?.orgType;
  // 产品介绍、特色服务与重点工作：仅 tenantLevel 4/5/6 可见（可见范围与产品介绍一致）
  const showIntroSections = tenantLevel === 4 || tenantLevel === 5 || tenantLevel === 6;
  // orgType 1 = 企业；其他 = 政务/其他类型
  const isEnterprise = orgType === 1;
  // 该页面企业与政务单位共用，文案按机构称谓自适应（企业/单位）
  const orgLabel = getOrgLabel(orgType, tenantInfo?.tenantType);
  const availableSectionOptions = sectionOptions.filter(o => {
    // 党建动态：仅 tenantLevel 6 可见
    if (o.value === 'partyBuildingNews') return tenantLevel === 6;
    // orgType=1（企业）：产品介绍、宣传视频 仅 tenantLevel 4/5/6 可见
    if (isEnterprise) {
      if (o.value === 'productIntro' || o.value === 'promoVideo') return showIntroSections;
      // 固定板块：文化展示、荣誉资质、领导关怀、经营资质、投资项目、企业实力、合作情况
      return ['cultureShow', 'honors', 'leadershipCare', 'qualifications', 'investmentProjects', 'companyStrength', 'cooperation'].includes(o.value);
    }
    // orgType=2（政务/其他）：特色服务与重点工作、宣传视频 仅 tenantLevel 4/5/6 可见
    if (o.value === 'featuredBiz' || o.value === 'promoVideo') return showIntroSections;
    // 固定板块：文化展示、荣誉资质、领导关怀、重点工作/民生实事、便民服务指南、共建共治合作单位
    return ['cultureShow', 'honors', 'leadershipCare', 'keyLivelihood', 'serviceGuide', 'coGovernance'].includes(o.value);
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await enterpriseSiteApi.get();
      if (res.code === 200 && res.data) {
        setConfig(mapApiToConfig(res.data));
        setHasData(true);
        setIsEditing(false);
      } else {
        setConfig(emptyConfig());
        setHasData(false);
        setIsEditing(true);
      }
    } catch (err) {
      setConfig(emptyConfig());
      setHasData(false);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    loadData();
  }, [loadData]);

  const toggleSection = (sectionValue: string) => {
    setConfig(prev => {
      const sections = prev.selectedSections || [];
      if (sections.includes(sectionValue)) {
        const newSections = sections.filter(s => s !== sectionValue);
        if (activeTab === sectionValue && newSections.length > 0) {
          setActiveTab(newSections[0]);
        } else if (newSections.length === 0) {
          setActiveTab('');
        }
        return { ...prev, selectedSections: newSections };
      } else {
        const newSections = [...sections, sectionValue];
        if (sections.length === 0) {
          setActiveTab(sectionValue);
        }
        return { ...prev, selectedSections: newSections };
      }
    });
  };

  const handleTabDragSort = (context: { current: string; target: string; currentIndex: number; targetIndex: number }) => {
    setConfig(prev => {
      const sections = [...(prev.selectedSections || [])];
      const { currentIndex, targetIndex } = context;
      if (currentIndex === targetIndex) return prev;
      const [movedItem] = sections.splice(currentIndex, 1);
      sections.splice(targetIndex, 0, movedItem);
      return { ...prev, selectedSections: sections };
    });
  };

  const handleTabRemove = (options: { value: string; index: number }) => {
    setConfig(prev => {
      const sections = [...(prev.selectedSections || [])];
      sections.splice(options.index, 1);
      return { ...prev, selectedSections: sections };
    });
    if (activeTab === options.value) {
      setActiveTab('');
    }
  };

  const handleSave = async () => {
    // 1. 基本信息必填校验
    if (!config.companyName.trim()) {
      MessagePlugin.warning(`请输入${orgLabel}名称`);
      return;
    }
    if (config.companyImages.length === 0) {
      MessagePlugin.warning(`请上传${orgLabel}图片`);
      return;
    }
    if (!config.companyDesc.trim()) {
      MessagePlugin.warning(`请输入${orgLabel}介绍`);
      return;
    }
    if (!isValidUrl(config.companyUrl)) {
      setUrlError('请输入正确的网址格式，如 https://www.example.com');
      return;
    }
    if (config.companyUrl) {
      setUrlError('');
    }

    // 2. 已勾选的自定义板块内容完整性校验
    const sections = config.selectedSections || [];
    const sectionLabelMap: Record<string, string> = {
      cultureShow: '文化展示', honors: '荣誉资质', leadershipCare: '领导关怀',
      qualifications: '经营资质', investmentProjects: '投资项目', companyStrength: '企业实力',
      cooperation: '合作情况', partyBuildingNews: '党建动态', productIntro: '产品介绍',
      featuredBiz: '特色服务与重点工作',
      keyLivelihood: '重点工作/民生实事', serviceGuide: '便民服务指南', coGovernance: '共建共治合作单位',
      promoVideo: '宣传视频', newMedia: '新媒体', supplementInfo: '补充信息',
    };

    for (const section of sections) {
      if (section === 'promoVideo') {
        if (!config.promoVideo) {
          MessagePlugin.warning(`请上传「${sectionLabelMap[section]}」的视频`);
          return;
        }
        continue;
      }

      // 图文类板块
      const imageTextFields: Record<string, ImageTextItem[]> = {
        cultureShow: config.cultureShow,
        honors: config.honors,
        leadershipCare: config.leadershipCare,
        qualifications: config.qualifications,
        cooperation: config.cooperation,
        partyBuildingNews: config.partyBuildingNews,
        productIntro: config.productIntro,
        featuredBiz: config.featuredBiz,
        keyLivelihood: config.keyLivelihood,
        serviceGuide: config.serviceGuide,
      };

      if (imageTextFields[section]) {
        const items = imageTextFields[section];
        if (items.length === 0) {
          MessagePlugin.warning(`「${sectionLabelMap[section]}」至少添加一条内容`);
          return;
        }
        for (let i = 0; i < items.length; i++) {
          if (section !== 'cultureShow' && !items[i].image) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请上传图片`);
            return;
          }
          if (!items[i].title.trim()) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请输入标题`);
            return;
          }
        }
      }

      // 图标类板块
      const iconTextFields: Record<string, IconTextItem[]> = {
        investmentProjects: config.investmentProjects,
        companyStrength: config.companyStrength,
      };

      if (iconTextFields[section]) {
        const items = iconTextFields[section];
        if (items.length === 0) {
          MessagePlugin.warning(`「${sectionLabelMap[section]}」至少添加一条内容`);
          return;
        }
        for (let i = 0; i < items.length; i++) {
          if (!items[i].icon) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请选择图标`);
            return;
          }
          if (!items[i].title.trim()) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请输入标题`);
            return;
          }
        }
      }

      // 共建共治合作单位：至少上传一张单位图片
      if (section === 'coGovernance') {
        if (!config.coGovernance || config.coGovernance.images.length === 0) {
          MessagePlugin.warning(`「${sectionLabelMap[section]}」请至少上传一张单位图片`);
          return;
        }
      }
    }

    // 3. 新媒体与补充信息（独立板块，选填）：数组项必填字段校验
    {
      const ms = config.mediaAndSupplement;
      for (let i = 0; i < ms.otherMediaAccounts.length; i++) {
        if (!ms.otherMediaAccounts[i].name.trim()) {
          MessagePlugin.warning(`「新媒体与补充信息」其他新媒体账号第${i + 1}条请输入账号名称`);
          return;
        }
      }
      for (let i = 0; i < ms.specialGroupServices.length; i++) {
        if (!ms.specialGroupServices[i].name.trim()) {
          MessagePlugin.warning(`「新媒体与补充信息」特殊群体服务第${i + 1}条请输入服务名称`);
          return;
        }
      }
      for (let i = 0; i < ms.feedbackChannels.length; i++) {
        if (!ms.feedbackChannels[i].name.trim()) {
          MessagePlugin.warning(`「新媒体与补充信息」反馈/监督渠道第${i + 1}条请输入渠道名称`);
          return;
        }
      }
      for (let i = 0; i < ms.holidayDuties.length; i++) {
        if (!ms.holidayDuties[i].description.trim()) {
          MessagePlugin.warning(`「新媒体与补充信息」值班安排第${i + 1}条请输入值班描述`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      const apiData = mapConfigToApi(config);
      const res = await enterpriseSiteApi.update(apiData);
      if (res.code === 200) {
        MessagePlugin.success(config.id ? '更新成功' : '保存成功');
        setIsEditing(false);
        setHasData(true);
        if (res.data) {
          setConfig(mapApiToConfig(res.data));
        } else {
          await loadData();
        }
      } else {
        MessagePlugin.error(res.message || '保存失败');
      }
    } catch (err: any) {
      MessagePlugin.error(err?.message || '保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasData) {
      setIsEditing(false);
      loadData();
    } else {
      loadData();
    }
    setUrlError('');
  };

  // ============ Tabs 内容渲染 ============
  const renderTabPanelContent = (sectionValue: string) => {
    switch (sectionValue) {
      case 'cultureShow':
        return (
          <TextRichEditor
            items={config.cultureShow}
            onChange={(v) => setConfig({ ...config, cultureShow: v })}
            colorClass="border-purple-200 bg-purple-50/30"
          />
        );
      case 'honors':
        return (
          <ImageRichEditor
            items={config.honors}
            onChange={(v) => setConfig({ ...config, honors: v })}
            colorClass="border-amber-200 bg-amber-50/30"
          />
        );
      case 'leadershipCare':
        return (
          <ImageRichEditor
            items={config.leadershipCare}
            onChange={(v) => setConfig({ ...config, leadershipCare: v })}
            colorClass="border-green-200 bg-green-50/30"
          />
        );
      case 'qualifications':
        return (
          <ImageRichEditor
            items={config.qualifications}
            onChange={(v) => setConfig({ ...config, qualifications: v })}
            colorClass="border-blue-200 bg-blue-50/30"
          />
        );
      case 'investmentProjects':
        return (
          <IconRichEditor
            items={config.investmentProjects}
            onChange={(v) => setConfig({ ...config, investmentProjects: v })}
          />
        );
      case 'companyStrength':
        return (
          <IconRichEditor
            items={config.companyStrength}
            onChange={(v) => setConfig({ ...config, companyStrength: v })}
          />
        );
      case 'cooperation':
        return (
          <ImageRichEditor
            items={config.cooperation}
            onChange={(v) => setConfig({ ...config, cooperation: v })}
            colorClass="border-pink-200 bg-pink-50/30"
          />
        );
      case 'partyBuildingNews':
        return (
          <ImageRichEditor
            items={config.partyBuildingNews}
            onChange={(v) => setConfig({ ...config, partyBuildingNews: v })}
            colorClass="border-red-200 bg-red-50/30"
          />
        );
      case 'productIntro':
        return (
          <ImageRichEditor
            items={config.productIntro}
            onChange={(v) => setConfig({ ...config, productIntro: v })}
            colorClass="border-indigo-200 bg-indigo-50/30"
          />
        );
      case 'featuredBiz':
        return (
          <ImageRichEditor
            items={config.featuredBiz}
            onChange={(v) => setConfig({ ...config, featuredBiz: v })}
            colorClass="border-pink-200 bg-pink-50/30"
          />
        );
      case 'keyLivelihood':
        return (
          <ImageRichEditor
            items={config.keyLivelihood}
            onChange={(v) => setConfig({ ...config, keyLivelihood: v })}
            colorClass="border-orange-200 bg-orange-50/30"
          />
        );
      case 'serviceGuide':
        return (
          <ImageRichEditor
            items={config.serviceGuide}
            onChange={(v) => setConfig({ ...config, serviceGuide: v })}
            colorClass="border-teal-200 bg-teal-50/30"
          />
        );
      case 'coGovernance':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 block mb-2">合作单位总数</label>
              <Input
                value={String(config.coGovernance?.count ?? 0)}
                onChange={(v) => setConfig({
                  ...config,
                  coGovernance: { ...config.coGovernance, count: Number(v) || 0 }
                })}
                placeholder="请输入合作单位总数，如：12"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-2">合作单位图片（可上传多张）</label>
              <ProductImageUploader
                value={config.coGovernance?.images?.join(',') || ''}
                onChange={(v) => setConfig({
                  ...config,
                  coGovernance: {
                    ...config.coGovernance,
                    images: typeof v === 'string' ? v.split(',').filter(Boolean) : (v as string[])
                  }
                })}
              />
            </div>
          </div>
        );
      case 'promoVideo':
        return (
          <div>
            <div className="text-xs text-gray-500 mb-3">上传一个宣传视频即可</div>
            <ProductImageUploader
              value={config.promoVideo}
              onChange={(v) => setConfig({ ...config, promoVideo: typeof v === 'string' ? v : '' })}
              uploadType={2}
              simple
            />
          </div>
        );
      case 'newMedia':
      case 'supplementInfo':
        // 新媒体 / 补充信息已在下方独立 SectionCard 中编辑，此处仅提示，避免重复编辑
        return (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 p-6 text-center">
            <div className="text-sm text-gray-600 mb-1">
              请在下方「{sectionValue === 'newMedia' ? '新媒体与补充信息' : '补充信息（选填）'}」板块中编辑
            </div>
            <div className="text-xs text-gray-400">该板块为独立模块，此处仅作为是否在名片中展示的开关</div>
          </div>
        );
      default:
        return null;
    }
  };

  // ============ 编辑模式渲染 ============
  const renderEditMode = () => (
    <div className="space-y-4">
      {/* 基本信息 */}
      <SectionCard title={`${orgLabel}基本信息`} icon={Building2}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-sm text-gray-500 block mb-2">{orgLabel}名称<span className="text-red-500 ml-1">*</span></label>
            <Input
              value={config.companyName}
              onChange={(v) => setConfig({ ...config, companyName: v })}
              placeholder={`请输入${orgLabel}名称`}
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm text-gray-500 block mb-2">{orgLabel}网址</label>
            <Input
              value={config.companyUrl}
              onChange={(v) => {
                setConfig({ ...config, companyUrl: v });
                if (v && !isValidUrl(v)) {
                  setUrlError('请输入正确的网址格式，如 https://www.example.com');
                } else {
                  setUrlError('');
                }
              }}
              placeholder={`请输入${orgLabel}网址，如 https://www.example.com`}
            />
            {urlError && <div className="text-xs text-red-500 mt-1">{urlError}</div>}
          </div>
          <div className="col-span-2">
            <label className="text-sm text-gray-500 block mb-2">{orgLabel}图片（可多张）<span className="text-red-500 ml-1">*</span></label>
            <ProductImageUploader
              value={config.companyImages.length > 0 ? config.companyImages.join(',') : ''}
              onChange={(v) => setConfig({
                ...config,
                companyImages: typeof v === 'string' ? v.split(',').filter(Boolean) : (v as string[])
              })}
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm text-gray-500 block mb-2">{orgLabel}介绍<span className="text-red-500 ml-1">*</span></label>
            <ProductRichTextEditor
              value={config.companyDesc}
              onChange={(v) => setConfig({ ...config, companyDesc: v })}
            />
          </div>
        </div>
      </SectionCard>

      {/* 信息自定义选择配置 */}
      <SectionCard title={`${orgLabel}信息自定义选择配置`} icon={Sparkles}>
        <p className="text-xs text-gray-500 mb-4">{orgLabel}信息自定义选择配置展示</p>

        {/* 选择区域 */}
        <div className="mb-4">
          <div className="text-xs text-gray-600 mb-2 font-medium">可选内容</div>
          <div className="grid grid-cols-5 gap-3">
            {availableSectionOptions.map(option => {
              const isSelected = config.selectedSections?.includes(option.value);
              const IconComp = option.icon;
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected
                      ? 'border-[#1c5bff] bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <Checkbox checked={isSelected} onChange={() => toggleSection(option.value)} />
                  <IconComp className={`w-4 h-4 ${isSelected ? 'text-[#1c5bff]' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isSelected ? 'text-[#1c5bff] font-medium' : 'text-gray-700'}`}>
                    {option.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Tabs 排列区域 */}
        {config.selectedSections && config.selectedSections.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-gray-600 font-medium">
                已选内容 ({config.selectedSections.length} 项)
              </div>
              <span className="text-[10px] text-gray-400">支持拖拽排序、点击删除</span>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <Tabs
                value={activeTab || config.selectedSections[0]}
                onChange={(val) => setActiveTab(val as string)}
                dragSort
                scrollPosition="auto"
                theme="card"
                size="medium"
                onDragSort={handleTabDragSort}
                onRemove={handleTabRemove}
                className="custom-tabs"
              >
                {config.selectedSections.map((sectionValue) => {
                  const option = sectionOptions.find(o => o.value === sectionValue);
                  if (!option) return null;
                  const IconComp = option.icon;

                  const tabLabel = (
                    <span className="inline-flex items-center gap-1.5 py-1">
                      <IconComp className="w-3.5 h-3.5 text-[#1c5bff]" />
                      <span className="text-xs font-medium">{option.label}</span>
                    </span>
                  );

                  return (
                    <Tabs.TabPanel
                      key={sectionValue}
                      value={sectionValue}
                      label={tabLabel}
                      removable
                    >
                      <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
                        {renderTabPanelContent(sectionValue)}
                      </div>
                    </Tabs.TabPanel>
                  );
                })}
              </Tabs>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50/50">
            暂未选择任何内容，请在上方选择需要展示的页面
          </div>
        )}
      </SectionCard>

      {/* 新媒体与补充信息（独立板块） */}
      {orgType == 2 ? (
        <SectionCard title="新媒体与补充信息" icon={Newspaper}>
          <MediaAndSupplementEditor
            value={config.mediaAndSupplement}
            onChange={(v) => setConfig({ ...config, mediaAndSupplement: v })}
          />
        </SectionCard>
      ) : (
        <SectionCard title="补充信息（选填）" icon={Globe}>
          <SupplementInfoEditor
            value={config.supplementInfo}
            onChange={(v) => setConfig({ ...config, supplementInfo: v })}
          />
        </SectionCard>
      )}
    </div>
  );

  // ============ 展示模式渲染 ============
  const renderDisplayMode = () => (
    <div className="space-y-4">
      <div className="flex  gap-4">
      {/* 基本信息 */}
      <SectionCard title={`${orgLabel}基本信息`} icon={Building2}>
        <DisplayRow label={`${orgLabel}名称`} value={config.companyName} />
        <DisplayRow
          label={`${orgLabel}网址`}
          value={config.companyUrl ? (
            <a href={config.companyUrl} target="_blank" rel="noopener noreferrer" className="text-[#1c5bff] hover:underline flex items-center gap-1 justify-end">
              <ExternalLink className="w-3 h-3" />
              <span className="truncate max-w-[200px]">{config.companyUrl}</span>
            </a>
          ) : '-'}
        />
        <div className="py-2 border-b border-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{orgLabel}图片</span>
            <span className="text-sm text-gray-800 font-medium">
              {config.companyImages.length > 0 ? (
                <div className="flex items-center gap-1">
                  {config.companyImages.slice(0, 4).map((img, idx) => (
                    <PreviewableImage key={idx} src={img} alt={`${orgLabel}图片${idx + 1}`} className="w-12 h-12 object-cover rounded border" />
                  ))}
                  {config.companyImages.length > 4 && (
                    <span className="text-xs text-gray-400 ml-1">+{config.companyImages.length - 4}</span>
                  )}
                </div>
              ) : '-'}
            </span>
          </div>
        </div>
        <div className="py-2 last:border-0">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{orgLabel}介绍</span>
            <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
              {config.companyDesc ? (
                <div
                  className="line-clamp-3 text-left"
                  dangerouslySetInnerHTML={{ __html: config.companyDesc }}
                />
              ) : '-'}
            </span>
          </div>
        </div>
        {config.updatedAt && (
          <DisplayRow label="更新时间" value={config.updatedAt} />
        )}
      </SectionCard>

      {/* 自定义选择配置展示 */}
      {config.selectedSections && config.selectedSections.length > 0 && (
        <SectionCard title="自定义选择配置" icon={Sparkles}>
          <div className="flex flex-wrap gap-2 mb-4">
            {config.selectedSections.map((sectionValue, idx) => {
              const option = sectionOptions.find(o => o.value === sectionValue);
              if (!option) return null;
              const IconComp = option.icon;
              return (
                <span key={sectionValue} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-[#1c5bff] border border-[#1c5bff]/30 text-xs">
                  <span className="w-4 h-4 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center">{idx + 1}</span>
                  <IconComp className="w-3 h-3" />
                  {option.label}
                </span>
              );
            })}
          </div>

          {/* {config.selectedSections.includes('cultureShow') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Sparkles className="w-4 h-4 text-purple-500" /> 文化展示
              </div>
              <ImageRichDisplay items={config.cultureShow} emptyText="暂无文化展示内容" />
            </div>
          )}
          {config.selectedSections.includes('honors') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Award className="w-4 h-4 text-amber-500" /> 荣誉资质
              </div>
              <ImageRichDisplay items={config.honors} emptyText="暂无荣誉资质内容" />
            </div>
          )}
          {config.selectedSections.includes('leadershipCare') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Users className="w-4 h-4 text-green-500" /> 领导关怀
              </div>
              <ImageRichDisplay items={config.leadershipCare} emptyText="暂无领导关怀内容" />
            </div>
          )}
          {config.selectedSections.includes('qualifications') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <FileCheck className="w-4 h-4 text-blue-500" /> 经营资质
              </div>
              <ImageRichDisplay items={config.qualifications} emptyText="暂无经营资质内容" />
            </div>
          )}
          {config.selectedSections.includes('investmentProjects') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Target className="w-4 h-4 text-indigo-500" /> 投资项目
              </div>
              <IconRichDisplay items={config.investmentProjects} emptyText="暂无投资项目内容" />
            </div>
          )}
          {config.selectedSections.includes('companyStrength') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Building2 className="w-4 h-4 text-teal-500" /> 企业实力
              </div>
              <IconRichDisplay items={config.companyStrength} emptyText="暂无企业实力内容" />
            </div>
          )}
          {config.selectedSections.includes('cooperation') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <HeartHandshake className="w-4 h-4 text-pink-500" /> 合作情况
              </div>
              <ImageRichDisplay items={config.cooperation} emptyText="暂无合作情况内容" />
            </div>
          )}
          {config.selectedSections.includes('partyBuildingNews') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Flag className="w-4 h-4 text-red-500" /> 党建动态
              </div>
              <ImageRichDisplay items={config.partyBuildingNews} emptyText="暂无党建动态内容" />
            </div>
          )}
          {config.selectedSections.includes('productIntro') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Package className="w-4 h-4 text-indigo-500" /> 产品介绍
              </div>
              <ImageRichDisplay items={config.productIntro} emptyText="暂无产品介绍内容" />
            </div>
          )}
          {config.selectedSections.includes('promoVideo') && (
            <div className="mb-2">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Video className="w-4 h-4 text-cyan-500" /> 宣传视频
              </div>
              {config.promoVideo ? (
                <video
                  src={config.promoVideo}
                  controls
                  className="w-full max-w-md rounded-lg border border-gray-200"
                />
              ) : <span className="text-gray-400 text-sm">-</span>}
            </div>
          )} */}
        </SectionCard>
      )}
      </div>

      {/* 新媒体与补充信息 */}
      {orgType == 2 ? (
        <SectionCard title="新媒体与补充信息" icon={Newspaper}>
          <DisplayRow label="微信公众号" value={config.mediaAndSupplement?.wechatOfficial?.name || '-'} />
          <DisplayRow label="微信视频号" value={config.mediaAndSupplement?.wechatVideo?.name || '-'} />
          <div className="py-2 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">其他新媒体账号</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
                {config.mediaAndSupplement?.otherMediaAccounts?.length
                  ? config.mediaAndSupplement.otherMediaAccounts.map(i => i.name).filter(Boolean).join('、')
                  : '-'}
              </span>
            </div>
          </div>
          <div className="py-2 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">特殊群体服务</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
                {config.mediaAndSupplement?.specialGroupServices?.length
                  ? config.mediaAndSupplement.specialGroupServices.map(i => i.name).filter(Boolean).join('、')
                  : '-'}
              </span>
            </div>
          </div>
          <div className="py-2 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">意见反馈/监督渠道</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
                {config.mediaAndSupplement?.feedbackChannels?.length
                  ? config.mediaAndSupplement.feedbackChannels.map(i => i.name).filter(Boolean).join('、')
                  : '-'}
              </span>
            </div>
          </div>
          <div className="py-2 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">节假日值班安排</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
                {config.mediaAndSupplement?.holidayDuties?.length
                  ? `${config.mediaAndSupplement.holidayDuties.length} 项`
                  : '-'}
              </span>
            </div>
          </div>
          <div className="py-2 last:border-0">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">其他内容</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
                {config.mediaAndSupplement?.otherContent ? (
                  <div className="line-clamp-3 text-left" dangerouslySetInnerHTML={{ __html: config.mediaAndSupplement.otherContent }} />
                ) : '-'}
              </span>
            </div>
          </div>
        </SectionCard>
      ) : (
        <SectionCard title="补充信息" icon={Newspaper}>
          {/* 官方网站 */}
          {config.supplementInfo?.officialWebsite?.name || config.supplementInfo?.officialWebsite?.url ? (
            <div className="py-2 border-b border-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">官方网站</span>
                <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
                  {config.supplementInfo.officialWebsite.name || '-'}
                  {config.supplementInfo.officialWebsite.url && (
                    <a
                      href={config.supplementInfo.officialWebsite.url}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 text-[#1c5bff] hover:underline break-all"
                    >
                      {config.supplementInfo.officialWebsite.url}
                    </a>
                  )}
                </span>
              </div>
            </div>
          ) : (
            <DisplayRow label="官方网站" value="-" />
          )}

          {/* 社交媒体账号 */}
          <div className="py-2 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">社交媒体账号</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
                {config.supplementInfo?.socialMediaAccounts?.length
                  ? config.supplementInfo.socialMediaAccounts.map(i => i.name).filter(Boolean).join('、')
                  : '-'}
              </span>
            </div>
            {config.supplementInfo?.socialMediaAccounts?.filter(i => i.image)?.length ? (
              <div className="flex flex-wrap gap-2 justify-end mt-2">
                {config.supplementInfo.socialMediaAccounts.filter(i => i.image).map((i, idx) => (
                  <PreviewableImage key={i.id || idx} src={i.image} alt={i.name || '社交媒体账号'} className="w-14 h-14 rounded-lg border border-gray-200 object-cover" />
                ))}
              </div>
            ) : null}
          </div>

          {/* 其他展示内容 */}
          <div className="py-2 last:border-0">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">其他展示内容</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
                {config.supplementInfo?.otherContent ? (
                  <div className="line-clamp-3 text-left" dangerouslySetInnerHTML={{ __html: config.supplementInfo.otherContent }} />
                ) : '-'}
              </span>
            </div>
          </div>
        </SectionCard>
      )}
    </div>
  );

  const showEditActions = isEditing;
  const showReadOnlyActions = !isEditing && hasData;

  return (
    <ImagePreviewProvider>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#1c5bff]" />
            微信官方网站配置
          </h1>
          {showReadOnlyActions && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 text-sm"
            >
              <Edit3 className="w-4 h-4" />
              编辑
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1c5bff] mr-3"></div>
            加载中...
          </div>
        ) : (
          <div className="space-y-4">
            {isEditing ? renderEditMode() : renderDisplayMode()}

            {showEditActions && (
              <div className="sticky bottom-0 bg-white border-t border-gray-100 rounded-xl shadow-sm p-4 flex items-center justify-end gap-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {config.id ? '更新' : '保存'}
                </button>
              </div>
            )}
          </div>
        )}

        <style>{`
          .custom-tabs .t-tabs__nav-item {
            padding: 8px 14px;
            border-radius: 8px 8px 0 0;
            transition: all 0.2s ease;
            gap: 4px;
            cursor: grab;
          }
          .custom-tabs .t-tabs__nav-item:active {
            cursor: grabbing;
          }
          .custom-tabs .t-tabs__nav-item:hover {
            background-color: #f5f5f5;
          }
          .custom-tabs .t-tabs__nav-item.t-is-active {
            font-weight: 600;
            color: #1c5bff;
            border-bottom: 2px solid #1c5bff;
          }
          .custom-tabs .t-tabs__nav-item.t-is-dragging {
            opacity: 0.5;
            background-color: #e3f2fd;
            box-shadow: 0 4px 12px rgba(28, 91, 255, 0.2);
          }
          .custom-tabs .t-tabs__nav-item .t-icon-close {
            margin-left: 4px;
            font-size: 12px;
          }
          .custom-tabs .t-tabs__nav-item .t-icon-close:hover {
            color: #ff4d4f;
          }
          .custom-tabs .t-tabs__content {
            padding-top: 12px;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item {
            border: 1px solid #e5e7eb;
            border-bottom: none;
            background: #fafafa;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item.t-is-active {
            background: #ffffff;
            border-color: #1c5bff;
            color: #1c5bff;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item.t-is-dragging {
            background: #e3f2fd;
            border-color: #1c5bff;
          }
          .custom-tabs .t-tabs__nav--card + .t-tabs__content {
            border: 1px solid #e5e7eb;
            border-top: none;
            border-radius: 0 0 8px 8px;
            background: #ffffff;
          }
        `}</style>
      </div>
    </ImagePreviewProvider>
  );
}